import React, { useState, useEffect } from "react";
import { useParams, useRouteMatch, Link } from "react-router-dom";
import {
  getProjectByIdAPI,
  importArtifactToProjectAPI,
  getArtifactsForUserAPI,
  deleteArtifactInProjectAPI,
  updateProjectAPI,
  publishProjectAPI,
  getProjectPublishingStatusAPI,
} from "views/APIs/ProjectsUtility";

import NotificationContext from "views/Components/Context.js";
import {
  Box,
  Typography,
  Divider,
  Chip,
  CircularProgress,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@material-ui/core";
import Button from "components/CustomButtons/Button.js";
import ArtifactCard from "./ArtifactCard";
import ArtifactTable from "../ArtifactTable";
import HeadingWithDescription from "components/Heading/HeadingWithDescription";
import { ImportExport, ReplayOutlined, DoneAll, Close } from "@material-ui/icons";
import IconButton from "@material-ui/core/IconButton";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ResponseViewer } from "../SolutionGenerator/ResponseViewer";

function SortableArtifactRow({ artifact, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: artifact.artifact_id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    display: "flex",
    alignItems: "center",
    padding: "8px 4px",
    borderBottom: "1px solid #eee",
    cursor: "default",
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <DragIndicatorIcon {...listeners} style={{ cursor: "grab", color: "#aaa", marginRight: 8 }} />
      <span style={{ flex: 1, fontSize: 14 }}>{artifact.artifact_name}</span>
      <Chip label={(artifact.artifact_type || "").replace(/_/g, " ")} size="small" variant="outlined" style={{ fontSize: 11, marginRight: 4 }} />
      <IconButton size="small" onClick={() => onRemove(artifact.artifact_id)} style={{ color: "#e57373" }} title="Exclude from publishing">
        <Close fontSize="small" />
      </IconButton>
    </div>
  );
}

export default function ProjectsBoard() {
  const projectId = useParams().projectID;
  const { showNotification } = React.useContext(NotificationContext);
  const [loading, setLoading] = useState(false);
  const [usersArtifacts, setUsersArtifacts] = useState([]);
  const [importArtifactModal, setImportArtifactModal] = useState(false);
  const path = useRouteMatch().path;
  const projectPath = path.split("/").slice(0, -3).join("/");
  const [detailsTab, setDetailsTab] = useState(0); // 0 = Plan, 1 = Artifacts

  const [selectedArtifactIds, setSelectedArtifactIds] = useState([]);
  const [selectedTypeFilters, setSelectedTypeFilters] = useState([]);

  const [projectDetails, setProjectDetails] = useState({
    name: "",
    description: "",
    additional_artifacts: [],
  });

  const [plan, setPlan] = useState(null);
  const [publishAlert, setPublishAlert] = useState(false);
  const [isAlreadyPublished, setIsAlreadyPublished] = useState(false);
  const [publishArtifactOrder, setPublishArtifactOrder] = useState([]);
  const [publishedCourseId, setPublishedCourseId] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const fetchProjectDetails = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      const response = await getProjectByIdAPI(projectId);
      setProjectDetails({
        name: response.name,
        description: response.description,
        additional_artifacts: response.additional_artifacts,
      });
      if (response?.message) {
        showNotification(response.message, "error");
        return;
      }
      setPlan(response.plan || null);
      try {
        const statusResponse = await getProjectPublishingStatusAPI(projectId);
        setIsAlreadyPublished(statusResponse.is_published === true);
        setPublishedCourseId(statusResponse.course_id || null);
      } catch (e) {
        setIsAlreadyPublished(false);
      }
      const artifactsResponse = await getArtifactsForUserAPI();
      setUsersArtifacts(
        Array.isArray(artifactsResponse) ? artifactsResponse : []
      );
    } catch (error) {
      showNotification(
        "Error fetching project details: " + error.message,
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectDetails();
  }, [projectId]);

  const handleDeleteArtifact = async (artifactId) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("artifact_id", artifactId);
      const response = await deleteArtifactInProjectAPI(formData);
      setProjectDetails(response);
      setProjectDetails((prev) => ({
        ...prev,
        additional_artifacts: prev?.additional_artifacts?.filter(
          (artifact) => artifact.artifact_id !== artifactId
        ),
      }));
      showNotification("Artifact removed successfully", "success");
    } catch (error) {
      showNotification("Error removing artifact: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleImportArtifact = async (
    artifactId,
    artifactType,
    artifactName,
    artifactDescription
  ) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("artifact_id", artifactId);
      formData.append("artifact_type", artifactType);
      formData.append("artifact_name", artifactName);
      formData.append("artifact_description", artifactDescription);
      const response = await importArtifactToProjectAPI(formData);
      setProjectDetails(response);

      showNotification("Artifact imported successfully", "success");
    } catch (error) {
      showNotification("Error importing artifact: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const artifactTypeToPathMapping = {
    CONCEPT_LABS: "/labdesign/lab/",
    FORM: "/form-design/",
    MODEL_DEVELOPMENT: "/model_development/model/",
    MODEL_VALIDATION: "/model_validation/model/",
    COURSE: "/course-design/courses/",
    RESEARCH_REPORT: "/research_report/",
    WHITE_PAPER: "/white_paper/",
    BLOG: "/blog/",
    NEWS_LETTER: "/news_letter/",
    CASE_STUDY: "/case_study/",
    HANDOUT: "/handout/",
    KEY_INSIGHTS: "/key_insights/",
    RESEARCH_REPORT: "/research_report/",
    PODCAST: "/podcastdesign/podcast/",
    PROJECT_PLAN: "/project_plan/",
    SESSION: "/sessions/session/",
  };

  // Function to format artifact type names for display
  const artifactTypeToNameMapping = {
    concept_labs: "Concept Labs",
    form: "Forms",
    model_development: "Model Development",
    model_validation: "Model Validation",
    course: "Course",
    research_report: "Research Report",
    white_paper: "White Paper",
    blog: "Blog",
    news_letter: "News Letter",
    case_study: "Case Study",
    handout: "Handout",
    key_insights: "Key Insights",
    research_report: "Research Report",
    podcast: "Podcast",
    project_plan: "Project Plan",
    session: "Session",
  };
  const formatArtifactTypeName = (type) => {
    return artifactTypeToNameMapping[type.toLowerCase()];
  };

  // Group artifacts by type
  const groupedArtifacts =
    projectDetails?.additional_artifacts?.reduce((groups, artifact) => {
      const type = artifact.artifact_type || "UNCATEGORIZED";
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(artifact);
      return groups;
    }, {}) || {};

  // Sort the group keys for consistent ordering
  const sortedGroupKeys = Object.keys(groupedArtifacts).sort((a, b) =>
    artifactTypeToNameMapping[a.toLowerCase()].localeCompare(
      artifactTypeToNameMapping[b.toLowerCase()]
    )
  );

  const openImportArtifactModal = () => {
    setSelectedArtifactIds([]);
    setSelectedTypeFilters([]);
    setImportArtifactModal(true);
  };

  const handleImportArtifactModalClose = () => {
    setImportArtifactModal(false);
    setSelectedArtifactIds([]);
    setSelectedTypeFilters([]);
  };

  // Derived: unique artifact types for chip filters
  const artifactTypes = Array.from(
    new Set(usersArtifacts.map((a) => a.artifact_type).filter(Boolean))
  ).sort();

  // Derived: rows passed to ArtifactTable, pre-filtered by active type chips
  const filteredArtifacts =
    selectedTypeFilters.length === 0
      ? usersArtifacts
      : usersArtifacts.filter((a) =>
          selectedTypeFilters.includes(a.artifact_type)
        );

  const handlePublishProject = async () => {
    setPublishAlert(false);
    try {
      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("artifact_order", JSON.stringify(publishArtifactOrder.map((a) => a.artifact_id)));
      await publishProjectAPI(formData);
      showNotification(
        "Your project is marked for publishing. You will be notified shortly.",
        "success"
      );
    } catch (error) {
      showNotification("Error publishing project: " + error.message, "error");
    }
  };

  const handleUpdateProject = async (updatedData) => {
    try {
      const formData = new FormData();
      formData.append("project_id", projectId);
      formData.append("project_name", updatedData.title);
      formData.append("project_description", updatedData.shortDescription);
      const response = await updateProjectAPI(formData);

      setProjectDetails((prev) => ({
        ...prev,
        name: updatedData.title,
        description: updatedData.shortDescription,
      }));
      setPlan(updatedData.plan);
      showNotification("Project updated successfully", "success");
    } catch (error) {
      showNotification("Error updating project: " + error.message, "error");
    }
  };


  return (
    <div>
      {isAlreadyPublished && publishedCourseId && (
        <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 12, padding: "8px 12px", background: "#f0faf0", borderRadius: 6, border: "1px solid #c8e6c9" }}>
          <DoneAll style={{ color: "#4caf50" }} />
          <Typography variant="body2" style={{ color: "#2e7d32", fontWeight: 600 }}>Published</Typography>
          <a
            href={`https://quskillbridge.qusandbox.com/course-information/${publishedCourseId}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#1976d2", fontSize: 14, marginLeft: 8 }}
          >
            View Published Course →
          </a>
        </Box>
      )}
      <Divider style={{ marginBottom: 20 }} />
      <HeadingWithDescription
        title={projectDetails.name}
        shortDescription={projectDetails.description}
        showImportIcon={true}
        showPublishIcon={true}
        setImportModal={setImportArtifactModal}
        setPublishAlert={(val) => {
          if (val) setPublishArtifactOrder([...(projectDetails.additional_artifacts || [])]);
          setPublishAlert(val);
        }}
        identifier="Project"
        onChange={(updatedData) => {
          handleUpdateProject(updatedData);
        }}
      />

      <Dialog open={publishAlert} onClose={() => setPublishAlert(false)}>
        <DialogTitle>Publish Project</DialogTitle>
        <DialogContent>
          {isAlreadyPublished ? (
            <DialogContentText style={{ marginBottom: 12 }}>
              This project has already been published. Publishing again will overwrite the existing course. Drag to set the module order below.
            </DialogContentText>
          ) : (
            <DialogContentText style={{ marginBottom: 12 }}>
              Drag to set the order in which artifacts will appear as modules in the published course, then confirm.
            </DialogContentText>
          )}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (active.id !== over?.id) {
                setPublishArtifactOrder((items) => {
                  const oldIndex = items.findIndex((i) => i.artifact_id === active.id);
                  const newIndex = items.findIndex((i) => i.artifact_id === over.id);
                  return arrayMove(items, oldIndex, newIndex);
                });
              }
            }}
          >
            <SortableContext items={publishArtifactOrder.map((a) => a.artifact_id)} strategy={verticalListSortingStrategy}>
              <div style={{ border: "1px solid #eee", borderRadius: 4 }}>
                {publishArtifactOrder.map((artifact) => (
                  <SortableArtifactRow
                    key={artifact.artifact_id}
                    artifact={artifact}
                    onRemove={(id) => setPublishArtifactOrder((items) => items.filter((a) => a.artifact_id !== id))}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishAlert(false)}>Cancel</Button>
          <Button
            color="quCreateColor"
            variant="contained"
            onClick={handlePublishProject}
          >
            {isAlreadyPublished ? "Overwrite & Publish" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>
      <Divider style={{ marginBottom: 20, marginTop: 20 }} />
      {plan && (
        <Tabs
          value={detailsTab}
          onChange={(_, idx) => setDetailsTab(idx)}
          indicatorColor="primary"
          textColor="primary"
          style={{ marginBottom: 16 }}
        >
          <Tab label={<Typography variant="body1">Plan</Typography>} />
          <Tab
            label={
              <Typography
                variant="body1"
                style={{ display: "flex", alignItems: "center" }}
              >
                Your project assets
              </Typography>
            }
          />
          <ReplayOutlined
            fontSize="small"
            style={{
              marginLeft: "auto",
              fontSize: "26px",
              marginTop: 8,
              cursor: "pointer",
            }}
            onClick={(event) => {
              const target = event.target.closest("svg");
              if (target) {
                target.style.transition = "transform 0.5s ease";
                target.style.transform = "rotate(-360deg)";
              }

              fetchProjectDetails();
            }}
          />
        </Tabs>
      )}

      <Dialog
        open={importArtifactModal}
        onClose={handleImportArtifactModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Import Artifact</DialogTitle>

        <DialogContent>
          {loading && (
            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 12 }}>
              <CircularProgress size={18} />
              <span>Loading…</span>
            </Box>
          )}

          {usersArtifacts.length === 0 && !loading ? (
            <Typography variant="body2">No artifacts available.</Typography>
          ) : (
            <>
              <Box display="flex" flexWrap="wrap" style={{ gap: 8, marginBottom: 16 }}>
                {artifactTypes.map((type) => {
                  const isActive = selectedTypeFilters.includes(type);
                  return (
                    <Chip
                      key={type}
                      label={formatArtifactTypeName(type) || type.replace(/_/g, " ")}
                      clickable
                      color={isActive ? "primary" : "default"}
                      variant={isActive ? "default" : "outlined"}
                      onClick={() =>
                        setSelectedTypeFilters((prev) =>
                          prev.includes(type)
                            ? prev.filter((t) => t !== type)
                            : [...prev, type]
                        )
                      }
                    />
                  );
                })}
              </Box>

              <ArtifactTable
                data={filteredArtifacts}
                selectedIds={selectedArtifactIds}
                onChangeSelected={setSelectedArtifactIds}
              />
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleImportArtifactModalClose}>Cancel</Button>
          <Button
            color="quCreateColor"
            variant="contained"
            disabled={selectedArtifactIds.length === 0 || loading}
            onClick={async () => {
              if (selectedArtifactIds.length === 0) return;
              for (const id of selectedArtifactIds) {
                const artifact = usersArtifacts.find((a) => a.artifact_id === id);
                if (!artifact) continue;
                await handleImportArtifact(
                  artifact.artifact_id,
                  artifact.artifact_type,
                  artifact.artifact_name,
                  artifact.artifact_description
                );
              }
              setImportArtifactModal(false);
              setSelectedArtifactIds([]);
              setSelectedTypeFilters([]);
            }}
          >
            {loading
              ? "Importing…"
              : `Import ${selectedArtifactIds.length} Artifact${selectedArtifactIds.length !== 1 ? "s" : ""}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grouped Artifacts Display */}
      {/* Grouped Artifacts Display OR Plan */}
      {!plan || detailsTab === 1 ? (
        projectDetails?.additional_artifacts?.length === 0 ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2">
                No artifacts imported yet. Click on{" "}
                <ImportExport style={{ fontSize: "14px" }} /> to import
                artifacts to the project.
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <>
            {sortedGroupKeys.map((artifactType) => (
              <div key={artifactType} style={{ marginBottom: 16 }}>
                <Typography
                  variant="h6"
                  style={{
                    marginBottom: 16,
                    borderBottom: "2px solid #e0e0e0",
                    paddingBottom: 8,
                    textDecoration: "none",
                    textTransform: "none",
                  }}
                >
                  {formatArtifactTypeName(artifactType)}
                </Typography>
                <Grid container spacing={2}>
                  {groupedArtifacts[artifactType].map((artifact) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      lg={4}
                      key={artifact.artifact_id}
                    >
                      <Link
                        to={{
                          pathname:
                            projectPath +
                            artifactTypeToPathMapping[
                              artifact.artifact_type.toUpperCase()
                            ] +
                            artifact.artifact_id,
                        }}
                      >
                        <ArtifactCard
                          name={artifact.artifact_name}
                          description={artifact.artifact_description}
                          handleDeleteArtifact={handleDeleteArtifact}
                          artifact_id={artifact.artifact_id}
                          artifact_type={artifact.artifact_type}
                        />
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </div>
            ))}
          </>
        )
      ) : (
        <Box>
          <ResponseViewer
            response={plan}
            loading={loading}
            setResponse={setPlan}
          />
        </Box>
      )}
    </div>
  );
}
